import { defineStore } from "pinia";
import { CommonHttp, SingleIdRequest } from "@/models/interfaces/http/common.http.request";
import { reactive, shallowReactive } from "vue";
import { rootStore } from "../index";
import { CommonEntity } from "@/models/interfaces/common";
import { RenameAsker } from "@/models/interfaces/entity-settings";
import { StoragePermissionsContainer, StorageAccessContainer, StorageWithItemsResponse, StorageGroupsWithFiguresResponse, 
  StoragePermissionsSetRequest, ItemMeasureUnitSetRequest, StorageResponsibleWorkerEditRequest, StorageWithItems, 
  StorageGroupWithFigures} from "@/models/interfaces/storage/storage-models";


/**
 * Методы АПИ управления складами и группами складов
 */

// API методы по версиям

// v 12.1.0.0
// getStoragesWithItems → GET api/storage/storages/WithItems
// createStorageGroup → POST api/storage/storage-groups
// editStorageGroup → PUT api/storage/storage-groups
// deleteStorageGroup → DELETE api/storage/storage-groups/{groupId}

// v 12.1.1.0
// createStorage → POST api/storage/storages
// renameStorage → PUT api/storage/storages/Rename
// editStorageResponsibleWorker → PUT api/storage/storages/EditResponsibleWorker
// deleteStorage → DELETE api/storage/storages/{storageId}

// v 12.1.2.0
// getStoragesWithFigures → GET api/storage/storages/WithFigures
// addStorageToGroup → PUT api/storage/storages/{storageId}/Groups/{groupId}
// removeStorageFromGroup → DELETE api/storage/storages/{storageId}/Groups/

// v 12.1.5.0
// setStoragePermissions → PUT api/storage/access/permissions
// getStoragePermissions → GET api/storage/access/permissions
// getStorageAccess → GET api/storage/access


const API_URL = process.env.VUE_APP_API_URL

let storagesWithItemsLoad: Promise<unknown> | null = null;

interface QueryState {
    loading: boolean;
    // ignore the request result and don't mutate the model after the request is finished
    stop_request: boolean;

    // текущие особые разрешения для функций склада.
    storage_permissions: StoragePermissionsContainer;
    // контейнер с флагами доступа к функциям склада.
    storage_access: StorageAccessContainer | undefined;
    // список складов для создания движений.
    storages_with_items: StorageWithItemsResponse | undefined;
    // списки складов с фин. показателями, разделенные по вкладкам групп.
    storages_with_figures: StorageGroupsWithFiguresResponse;
    
    // новый добавленный склад.
    created_storage: Storage;
    // новая добавленная группа складов.
    created_storage_group: CommonEntity;

    // asynchronous queue
    reqQueue: Map<string, AbortController>;
}

export const storageStore = defineStore("storageStore", {
  state: () =>
    ({
        loading: false,
        // ignore the request result and don't mutate the model after the request is finished
        stop_request: false,
        // reactive filters change and update directly from interface
        storage_permissions: shallowReactive({} as StoragePermissionsContainer),
        storage_access: undefined,
        storages_with_items: undefined,
        storages_with_figures: shallowReactive({} as StorageGroupsWithFiguresResponse),

        created_storage: shallowReactive({} as Storage),
        created_storage_group: shallowReactive({} as CommonEntity),

        reqQueue: new Map()
    } as QueryState),

  actions: {

    // GET

    // GET storage permissions
    async getStoragePermissions() {
      const rootstore = rootStore()
      this.stop_request = false

      const entkey = 'storage_permissions';

      // abort all other entity/graph result connections because only one element can be displayed at a time
      Array.from(this.reqQueue.entries()).forEach(([key, value]) => {
        if (key === entkey) {
          console.log('abort conn '+key)
          this.reqQueue.get(key)?.abort();
          this.reqQueue.delete(key);
        }
      });

      const controller = new AbortController();
      this.reqQueue.set(entkey, controller);

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.get(`storage/access/permissions`, { signal: controller.signal }).then(res => {
          response = res
          this.reqQueue.delete(entkey);
        }).catch(err => {
          console.log(err);
          this.reqQueue.delete(entkey);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.storage_permissions = reactive({} as StoragePermissionsContainer);
          // reactive filters change and update directly from interface
          const { data } = response;
          this.storage_permissions = reactive(data);

          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // GET storage access flags
    async getStorageAccess() {
      if (this.storageAccess) { return { response_ok: true } }
      
      const rootstore = rootStore()
      this.stop_request = false

      /* const entkey = 'storage_access';

      // abort all other entity/graph result connections because only one element can be displayed at a time
      Array.from(this.reqQueue.entries()).forEach(([key, value]) => {
        if (key === entkey) {
          console.log('abort conn '+key)
          this.reqQueue.get(key)?.abort();
          this.reqQueue.delete(key);
        }
      });

      const controller = new AbortController();
      this.reqQueue.set(entkey, controller); */

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.get(`storage/access`/*, { signal: controller.signal }*/).then(res => {
          response = res
          // this.reqQueue.delete(entkey);
        }).catch(err => {
          console.log(err);
          // this.reqQueue.delete(entkey);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.storage_access = undefined;
          // reactive filters change and update directly from interface
          const { data } = response;
          this.storage_access = reactive(data);

          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // GET storages with items for movement
    async getStoragesWithItems() {
      const rootstore = rootStore()
      this.stop_request = false

      const entkey = 'storages_with_items';

      // abort all other entity/graph result connections because only one element can be displayed at a time
      Array.from(this.reqQueue.entries()).forEach(([key, value]) => {
        if (key === entkey) {
          console.log('abort conn '+key)
          this.reqQueue.get(key)?.abort();
          this.reqQueue.delete(key);
        }
      });

      const controller = new AbortController();
      this.reqQueue.set(entkey, controller);

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.get(`storage/storages/WithItems`, { signal: controller.signal }).then(res => {
          response = res
          this.reqQueue.delete(entkey);
        }).catch(err => {
          console.log(err);
          this.reqQueue.delete(entkey);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.storages_with_items = reactive({} as StorageWithItemsResponse);
          // reactive filters change and update directly from interface
          const { data } = response;

          console.log(data)
          this.storages_with_items = reactive(data);

          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // Склады нужны и вне аналитики, где initData не вызывается, поэтому грузим при первом обращении.
    // Параллельные вызовы ждут один запрос, иначе getStoragesWithItems оборвёт предыдущий
    async ensureStoragesWithItems() {
      if (this.storages_with_items !== undefined) return;
      if (!storagesWithItemsLoad) {
        storagesWithItemsLoad = this.getStoragesWithItems().finally(() => {
          storagesWithItemsLoad = null;
        });
      }
      await storagesWithItemsLoad;
    },

    // GET storages with figures
    async getStoragesWithFigures() {
      const rootstore = rootStore()
      this.stop_request = false

      const entkey = 'storages_with_figures';

      // abort all other entity/graph result connections because only one element can be displayed at a time
      Array.from(this.reqQueue.entries()).forEach(([key, value]) => {
        if (key === entkey) {
          console.log('abort conn '+key)
          this.reqQueue.get(key)?.abort();
          this.reqQueue.delete(key);
        }
      });

      const controller = new AbortController();
      this.reqQueue.set(entkey, controller);

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.get(`storage/storages/WithFigures`, { signal: controller.signal }).then(res => {
          response = res
          this.reqQueue.delete(entkey);
        }).catch(err => {
          console.log(err);
          this.reqQueue.delete(entkey);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.storages_with_figures = reactive({} as StorageGroupsWithFiguresResponse);
          // reactive filters change and update directly from interface
          const { data } = response;
          this.storages_with_figures = reactive(data);

          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // EDIT

    // PUT set storage permissions
    async setStoragePermissions(arg: StoragePermissionsSetRequest) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.put(`storage/access/permissions`, arg).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // POST create storage
    async createStorage() {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.post(`storage/storages`, {}).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.created_storage = reactive({} as Storage);
          // reactive filters change and update directly from interface
          const { data } = response;
          // this.created_storage = reactive(data);

          this.loading = false
          return reactive(data);
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return undefined
    },

    // PUT rename storage
    async renameStorage(arg: RenameAsker) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.put(`storage/storages/Rename`, arg).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // PUT edit storage responsible worker
    async editStorageResponsibleWorker(arg: StorageResponsibleWorkerEditRequest) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.put(`storage/storages/EditResponsibleWorker`, arg).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // PUT add storage to group
    async addStorageToGroup(storageId: number, groupId: number) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.put(`storage/storages/${storageId}/Groups/${groupId}`, {}).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // DELETE remove storage from group
    async removeStorageFromGroup(storageId: number) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.delete(`storage/storages/${storageId}/Groups/`).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // DELETE remove storage
    async deleteStorage(storageId: number) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.delete(`storage/storages/${storageId}`).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // GROUPS

    // POST create storage group
    async createStorageGroup() {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.post(`storage/storage-groups`, {}).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.created_storage_group = reactive({} as CommonEntity);
          // reactive filters change and update directly from interface
          const { data } = response;
          this.created_storage_group = reactive(data);

          this.loading = false
          return reactive(data)
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // PUT edit storage group
    async editStorageGroup(arg: RenameAsker) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.put(`storage/storage-groups`, arg).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // DELETE storage group
    async deleteStorageGroup(groupId: number) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.delete(`storage/storage-groups/${groupId}`).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    initData() {
      this.getStorageAccess();
      this.getStoragePermissions();
      this.getStoragesWithItems();
      this.getStoragesWithFigures();
    },

    clearSettings() {
      // reactive filters change and update directly from interface
      this.clearCommon();
    },

    clearCommon() {
      // reactive filters change and update directly from interface
    },
  },

  getters: {
    
    isLoading(state): boolean {
      return state.loading
    },

    storageAccess(state): StorageAccessContainer | undefined {
      return state.storage_access
    },

    storagesWithItems(state): StorageWithItems[] {
      return state.storages_with_items && state.storages_with_items.Storages && state.storages_with_items.Storages.length ? state.storages_with_items.Storages : []
    },

    storagesWithFigures(state): StorageGroupWithFigures[] {
      return state.storages_with_figures && state.storages_with_figures.Groups && state.storages_with_figures.Groups.length ? state.storages_with_figures.Groups : []
    }
  }
});